import type {
  ConsoleBrandDetail,
  ConsoleBrandPage,
  ConsoleCandidatePage,
  ConsoleOperations,
  ConsoleOverview,
  ConsoleReviewDetail,
  ConsoleReviewPage,
  ConsoleRevisionHistory,
  ConsoleRun,
  ConsoleRunPage,
  ConsoleTimeline,
} from '@rsecure/brand-contracts/console';
import type { SessionInfo } from '../../shared/session';

/** Thin fetch wrapper over the BFF. The browser only ever calls same-origin `/bff/*`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { accept: 'application/json', ...(init?.headers ?? {}) },
  });
  if (res.status === 401 && !path.startsWith('/bff/auth/')) onUnauthorized?.();
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? `request failed (${res.status})`);
  return body as T;
}

const q = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};
const post = <T>(path: string, body: unknown) =>
  request<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

export type Params = Record<string, string | number | undefined | null>;

export const api = {
  session: () => request<SessionInfo>('/bff/auth/session'),
  sendCode: (email: string) => post<{ sent: true }>('/bff/auth/send-code', { email }),
  verify: (email: string, code: string) => post('/bff/auth/verify', { email, code }),
  devLogin: (email: string) => post('/bff/auth/dev-login', { email }),
  logout: () => post('/bff/auth/logout', {}),
  overview: () => request<ConsoleOverview>('/bff/api/overview'),
  operations: () => request<ConsoleOperations>('/bff/api/operations'),
  brands: (p: Params) => request<ConsoleBrandPage>(`/bff/api/brands${q(p)}`),
  candidates: (p: Params) => request<ConsoleCandidatePage>(`/bff/api/candidates${q(p)}`),
  brand: (ref: string) => request<ConsoleBrandDetail>(`/bff/api/brands/${encodeURIComponent(ref)}`),
  revisions: (ref: string) =>
    request<ConsoleRevisionHistory>(`/bff/api/brands/${encodeURIComponent(ref)}/revisions`),
  timeline: (ref: string) =>
    request<ConsoleTimeline>(`/bff/api/brands/${encodeURIComponent(ref)}/timeline`),
  preview: (ref: string) =>
    request<{ info: PreviewInfo; renderer: string }>(
      `/bff/api/brands/${encodeURIComponent(ref)}/preview`,
    ),
  reviews: (p: Params) => request<ConsoleReviewPage>(`/bff/api/reviews${q(p)}`),
  review: (id: string) => request<ConsoleReviewDetail>(`/bff/api/reviews/${id}`),
  runs: (p: Params) => request<ConsoleRunPage>(`/bff/api/runs${q(p)}`),
  run: (id: string) => request<ConsoleRun>(`/bff/api/runs/${id}`),
};

export interface PreviewInfo {
  slug: string;
  displayName: string;
  language: string;
  primary: string;
  onPrimary: string;
  bandLogoUrl: string | null;
  whiteHeaderLogoUrl: string | null;
  fidelity: 'registered' | 'wordmark';
  source: 'v3_catalog_compat';
}
